import React, { useState, useEffect } from 'react';
import { 
  Save, RefreshCw, Shield, AlertTriangle, Globe, UserCheck, Activity, Search
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import PreContractService from '../../services/preContractService';

const DueDiligenceConfig = () => {
  const { success, error: showError, toasts, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Default State
  const [config, setConfig] = useState({
    checks: {
      sanctions: true,
      pep: true,
      adverseMedia: false,
      financialHealth: false
    },
    manualReviewTriggers: {
      highRiskVendor: true,
      restrictedJurisdiction: true,
      valueThreshold: 0
    }
  });

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await PreContractService.getDueDiligenceConfig();
      if (response.data) {
        // Merge with defaults to ensure structure exists
        setConfig({
            checks: { ...config.checks, ...response.data.checks },
            manualReviewTriggers: { ...config.manualReviewTriggers, ...response.data.manualReviewTriggers }
        });
      }
    } catch (err) {
      console.error(err);
      showError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = (section, key) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: !prev[section][key]
      }
    }));
  };

  const handleValueChange = (e) => {
    const val = e.target.value;
    setConfig(prev => ({
      ...prev,
      manualReviewTriggers: {
        ...prev.manualReviewTriggers,
        valueThreshold: val === '' ? 0 : parseInt(val, 10)
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await PreContractService.updateDueDiligenceConfig(config);
      success('Configuration saved successfully');
    } catch (err) {
      console.error(err);
      showError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const headerActions = [
    { label: '', icon: <RefreshCw size={18} />, onClick: loadConfig, variant: 'outline' },
    { label: saving ? 'Saving...' : 'Save Changes', icon: <Save size={18} />, onClick: handleSave, variant: 'primary', disabled: saving || loading }
  ];

  if (loading) return <div className="p-5 text-center">Loading settings...</div>;

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Due Diligence Configuration" 
        description="Configure automated screening checks and manual review triggers" 
        actions={headerActions} 
      />

      <div className="page-container">
        <div className="row">
            {/* LEFT COLUMN: AUTOMATED CHECKS */}
            <div className="col-lg-6 mb-4">
                <div className="card shadow-sm border-0 h-100">
                    <div className="card-header bg-white py-3 border-bottom">
                        <div className="d-flex align-items-center gap-2">
                            <Search size={20} className="text-primary" />
                            <h6 className="mb-0 fw-bold">Automated Screening Checks</h6>
                        </div>
                    </div>
                    <div className="card-body">
                        <p className="text-muted small mb-4">Enable external checks to run automatically when a new vendor is added or a contract is initiated.</p>
                        
                        <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom border-light">
                            <div className="d-flex align-items-center gap-3">
                                <div className="bg-light p-2 rounded text-danger"><Globe size={20} /></div>
                                <div>
                                    <div className="fw-bold">Sanctions & Watchlists</div>
                                    <div className="text-muted small">Global sanctions list screening (OFAC, EU, UN)</div>
                                </div>
                            </div>
                            <div className="form-check form-switch">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    style={{ width: '3em', height: '1.5em' }}
                                    checked={config.checks.sanctions}
                                    onChange={() => handleToggle('checks', 'sanctions')}
                                />
                            </div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom border-light">
                            <div className="d-flex align-items-center gap-3">
                                <div className="bg-light p-2 rounded text-warning"><UserCheck size={20} /></div>
                                <div>
                                    <div className="fw-bold">PEP Screening</div>
                                    <div className="text-muted small">Politically Exposed Persons verification</div>
                                </div>
                            </div>
                            <div className="form-check form-switch">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    style={{ width: '3em', height: '1.5em' }}
                                    checked={config.checks.pep}
                                    onChange={() => handleToggle('checks', 'pep')}
                                />
                            </div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom border-light">
                            <div className="d-flex align-items-center gap-3">
                                <div className="bg-light p-2 rounded text-info"><Activity size={20} /></div>
                                <div>
                                    <div className="fw-bold">Adverse Media</div>
                                    <div className="text-muted small">Scan for negative news and reputational risk</div>
                                </div>
                            </div>
                            <div className="form-check form-switch">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    style={{ width: '3em', height: '1.5em' }}
                                    checked={config.checks.adverseMedia}
                                    onChange={() => handleToggle('checks', 'adverseMedia')}
                                />
                            </div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-3">
                                <div className="bg-light p-2 rounded text-success"><Shield size={20} /></div>
                                <div>
                                    <div className="fw-bold">Financial Health</div>
                                    <div className="text-muted small">Credit score and bankruptcy checks</div>
                                </div>
                            </div>
                            <div className="form-check form-switch">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    style={{ width: '3em', height: '1.5em' }}
                                    checked={config.checks.financialHealth}
                                    onChange={() => handleToggle('checks', 'financialHealth')}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT COLUMN: MANUAL REVIEW TRIGGERS */}
            <div className="col-lg-6 mb-4">
                <div className="card shadow-sm border-0 h-100">
                    <div className="card-header bg-white py-3 border-bottom">
                        <div className="d-flex align-items-center gap-2">
                            <AlertTriangle size={20} className="text-warning" />
                            <h6 className="mb-0 fw-bold">Manual Review Triggers</h6>
                        </div>
                    </div>
                    <div className="card-body">
                        <p className="text-muted small mb-4">Define conditions that force a Compliance Officer to manually review a vendor/contract.</p>

                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="fw-bold">High Risk Vendors</label>
                                <div className="form-check form-switch">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        checked={config.manualReviewTriggers.highRiskVendor}
                                        onChange={() => handleToggle('manualReviewTriggers', 'highRiskVendor')}
                                    />
                                </div>
                            </div>
                            <p className="text-muted small">Force review if Vendor Risk Score is "High" or "Critical".</p>
                        </div>

                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="fw-bold">Restricted Jurisdictions</label>
                                <div className="form-check form-switch">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        checked={config.manualReviewTriggers.restrictedJurisdiction}
                                        onChange={() => handleToggle('manualReviewTriggers', 'restrictedJurisdiction')}
                                    />
                                </div>
                            </div>
                            <p className="text-muted small">Force review if vendor is based in a high-risk country.</p>
                        </div>

                        <div className="mb-3">
                             <label className="fw-bold mb-2">Contract Value Threshold (USD)</label>
                             <input 
                                type="number" 
                                className="form-control" 
                                placeholder="Enter amount (e.g. 100000)"
                                value={config.manualReviewTriggers.valueThreshold || ''}
                                onChange={handleValueChange}
                             />
                             <p className="text-muted small mt-2">Contracts above this value will always trigger a due diligence review. Set to 0 to disable.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default DueDiligenceConfig;